import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ITask } from "@/interfaces/dashboard.interface";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2 } from "lucide-react";

// Tag color options (from Figma design)
const tagColorOptions = [
  { value: "#13D480", label: "Verde" },
  { value: "#7846FF", label: "Roxo" },
  { value: "#2078D7", label: "Azul" },
  { value: "#F2C71E", label: "Amarelo" },
  { value: "#DC4426", label: "Laranja" },
];

// Mock users for assignees
const mockUsers = [
  { name: "Ricardo Portas", avatar: "" },
  { name: "Filemom Paulo", avatar: "" },
  { name: "Maria Silva", avatar: "" },
  { name: "João Santos", avatar: "" },
];

// Zod validation schema
const editTaskSchema = z.object({
  taskName: z.string().min(1, "Nome da tarefa é obrigatório"),
  description: z.string().min(1, "Descrição é obrigatória"),
  project: z.string().min(1, "Projeto é obrigatório"),
  stage: z.string().min(1, "Etapa é obrigatória"),
  column: z.enum(["todo", "doing", "done"]),
  tags: z.array(z.string()).min(1, "Selecione pelo menos uma tag"),
  assignees: z.array(z.string()).min(1, "Selecione pelo menos um responsável"),
});

type EditTaskFormData = z.infer<typeof editTaskSchema>;

interface EditTaskDialogProps {
  open: boolean;
  task: ITask | null;
  onClose: () => void;
  onSubmit: (task: ITask) => void;
  onDelete: (taskId: string) => void;
}

export default function EditTaskDialog({
  open,
  task,
  onClose,
  onSubmit,
  onDelete,
}: EditTaskDialogProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);

  const form = useForm<EditTaskFormData>({
    resolver: zodResolver(editTaskSchema),
    defaultValues: {
      taskName: "",
      description: "",
      project: "",
      stage: "",
      column: "todo",
      tags: [],
      assignees: [],
    },
  });

  // Pre-fill form when task changes
  useEffect(() => {
    if (open && task) {
      const tagColors = task.tags?.map((t) => t.color) || [];
      const assigneeNames = task.assignees?.map((a) => a.name) || [];

      form.reset({
        taskName: task.taskName,
        description: task.description || "",
        project: task.project,
        stage: task.stage,
        column: task.column || "todo",
        tags: tagColors,
        assignees: assigneeNames,
      });
      setSelectedTags(tagColors);
      setSelectedAssignees(assigneeNames);
    }
  }, [open, task, form]);

  const handleTagToggle = (color: string) => {
    setSelectedTags((prev) => {
      const newTags = prev.includes(color)
        ? prev.filter((t) => t !== color)
        : [...prev, color];
      form.setValue("tags", newTags);
      return newTags;
    });
  };

  const handleAssigneeToggle = (name: string) => {
    setSelectedAssignees((prev) => {
      const newAssignees = prev.includes(name)
        ? prev.filter((a) => a !== name)
        : [...prev, name];
      form.setValue("assignees", newAssignees);
      return newAssignees;
    });
  };

  const handleSubmit = (data: EditTaskFormData) => {
    if (!task) return;

    const updatedTask: ITask = {
      ...task,
      taskName: data.taskName,
      description: data.description,
      project: data.project,
      stage: data.stage,
      column: data.column,
      tags: data.tags.map((color) => ({ color, label: color })),
      assignees: data.assignees.map((name) => ({
        name,
        avatar: mockUsers.find((u) => u.name === name)?.avatar || "",
      })),
      responsible: {
        name: data.assignees[0] || "",
        avatar: "",
      },
      status: data.column === "done" ? "confirmado" : "pendente",
      checked: data.column === "done",
    };

    onSubmit(updatedTask);
    onClose();
  };

  const handleDelete = () => {
    if (!task) return;

    if (window.confirm("Tem certeza que deseja excluir esta tarefa?")) {
      onDelete(task.id);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-darkblue border-gray-600 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-lg font-semibold">
            Editar Tarefa
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-4">
            {/* Task Name */}
            <FormField
              control={form.control}
              name="taskName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white">Nome da Tarefa</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Digite o nome da tarefa"
                      className="bg-[#0C1427] border-gray-600 text-white"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white">Descrição</FormLabel>
                  <FormControl>
                    <textarea
                      {...field}
                      placeholder="Digite a descrição da tarefa"
                      rows={3}
                      className="w-full bg-[#0C1427] border border-gray-600 text-white rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Project */}
            <FormField
              control={form.control}
              name="project"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white">Projeto</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Nome do projeto"
                      className="bg-[#0C1427] border-gray-600 text-white"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Stage */}
            <FormField
              control={form.control}
              name="stage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white">Etapa</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Ex: Desenvolvimento, Produção, etc."
                      className="bg-[#0C1427] border-gray-600 text-white"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Column */}
            <FormField
              control={form.control}
              name="column"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white">Status</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="bg-[#0C1427] border-gray-600 text-white">
                        <SelectValue placeholder="Selecione o status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-darkblue border-gray-600">
                      <SelectItem value="todo" className="text-white hover:bg-[#0C1427]">
                        Para Fazer
                      </SelectItem>
                      <SelectItem value="doing" className="text-white hover:bg-[#0C1427]">
                        Fazendo
                      </SelectItem>
                      <SelectItem value="done" className="text-white hover:bg-[#0C1427]">
                        Concluídas
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Tags */}
            <div>
              <Label className="text-white mb-2 block">Tags de Cor</Label>
              <div className="space-y-2">
                {tagColorOptions.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`edit-tag-${option.value}`}
                      checked={selectedTags.includes(option.value)}
                      onCheckedChange={() => handleTagToggle(option.value)}
                    />
                    <label
                      htmlFor={`edit-tag-${option.value}`}
                      className="text-sm text-white flex items-center gap-2 cursor-pointer"
                    >
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: option.value }}
                      />
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
              {form.formState.errors.tags && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.tags.message}
                </p>
              )}
            </div>

            {/* Assignees */}
            <div>
              <Label className="text-white mb-2 block">Responsáveis</Label>
              <div className="space-y-2">
                {mockUsers.map((user) => (
                  <div key={user.name} className="flex items-center space-x-2">
                    <Checkbox
                      id={`edit-assignee-${user.name}`}
                      checked={selectedAssignees.includes(user.name)}
                      onCheckedChange={() => handleAssigneeToggle(user.name)}
                    />
                    <label
                      htmlFor={`edit-assignee-${user.name}`}
                      className="text-sm text-white cursor-pointer"
                    >
                      {user.name}
                    </label>
                  </div>
                ))}
              </div>
              {form.formState.errors.assignees && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.assignees.message}
                </p>
              )}
            </div>

            <DialogFooter className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                className="flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Excluir
              </Button>
              <div className="flex-1" />
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button type="submit">
                Salvar Alterações
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
